// useAuth.ts — single React hook owning the Firebase Auth user state and
// caching a fresh ID token. Consumers read `user` and `getIdToken` from it.

import React from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { getFirebaseAuth, isFirebaseConfigured } from "./firebaseClient";

export interface AuthState {
  ready: boolean;
  user: User | null;
  getIdToken: () => Promise<string>;
}

export function useAuth(): AuthState {
  const [user, setUser] = React.useState<User | null>(null);
  // Mock mode (no Firebase config) has nothing to wait for, so it starts ready —
  // as an initializer, not a setState inside the effect, which would cascade a render.
  const [ready, setReady] = React.useState(() => !isFirebaseConfigured());

  React.useEffect(() => {
    if (!isFirebaseConfigured()) {
      return;
    }

    const unsub = onAuthStateChanged(getFirebaseAuth(), (u) => {
      setUser(u);
      setReady(true);
    });

    return unsub;
  }, []);

  const getIdToken = React.useCallback(async () => {
    const currentUser = getFirebaseAuth().currentUser;

    if (!currentUser) {
      throw new Error("not_signed_in");
    }

    return currentUser.getIdToken();
  }, []);

  return { ready, user, getIdToken };
}
